/** @file Draw-order moves of one slot relative to another: a step (forward, backward, front, back) or a drop. */
import type { Id } from '@pose2d/format/types';

/** `slot` goes right in front of (`above`) or right behind (`below`) `ref`, the way slot.setDrawOrder takes it. */
export interface OrderMove {
  slot: Id;
  ref: Id;
  place: 'above' | 'below';
}

/** A step of one slot; orders are back-to-front (drawOrder[0] is drawn first, at the back). */
export type OrderStep = 'forward' | 'backward' | 'front' | 'back';

/** The move of `slot` one step forward/backward or to the front/back; null when it cannot move that way. */
function step(order: readonly Id[], slot: Id, how: OrderStep): OrderMove | null {
  const at = order.indexOf(slot);
  if (at < 0) return null;
  const refAt = { forward: at + 1, backward: at - 1, front: order.length - 1, back: 0 }[how];
  const ref = order[refAt];
  if (ref === undefined || refAt === at) return null;
  return { slot, ref, place: refAt > at ? 'above' : 'below' };
}

/** Dropping `dragged` on the row of `onto`: it takes that row's place, in front of it when it came from behind. */
function drop(order: readonly Id[], dragged: Id, onto: Id): OrderMove | null {
  const from = order.indexOf(dragged);
  const to = order.indexOf(onto);
  if (from < 0 || to < 0 || from === to) return null;
  return { slot: dragged, ref: onto, place: from < to ? 'above' : 'below' };
}

/** `order` after `move`, as a new array (the full permutation animation.setDrawOrderKey takes). */
function apply(order: readonly Id[], move: OrderMove): Id[] {
  const rest = order.filter((id) => id !== move.slot);
  const at = rest.indexOf(move.ref) + (move.place === 'above' ? 1 : 0);
  return [...rest.slice(0, at), move.slot, ...rest.slice(at)];
}

/** Pure draw-order move math for the Draw Order panel. */
export const orderMove = {
  /** One step of the selected slot. */
  step,
  /** A row dropped on another row. */
  drop,
  /** The full order after a move. */
  apply,
};
